/* ============================================================
   OrderTimeline — TASK-251: shared, role-aware order status
   timeline. Composes Flowwow-like stages (placed → accepted →
   printing → result → confirmed → shipped → completed) with
   real timestamps from existing API data, role-specific next
   actions, and graceful terminal/empty states. No fake data:
   a stage shows a timestamp only when the API actually has one.
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.

   TASK-248: the pure stage model (types, labels, progressIndex,
   buildOrderTimeline) moved to orderTimelineModel.ts so the derivation
   runs under `node --test` like every other view model; this file keeps
   only the two presenters (vertical detail timeline + provider strip)
   and re-exports the model for existing import sites.
   ============================================================ */

import { buildOrderTimeline } from './orderTimelineModel.ts';
import type { OrderTimelineInput, TimelineRole } from './orderTimelineModel.ts';

export type { TimelineRole, OrderTimelineInput, TimelineStage, OrderTimelineModel } from './orderTimelineModel.ts';
export { buildOrderTimeline } from './orderTimelineModel.ts';

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── Vertical timeline (customer order detail) ────────────── */

export function OrderTimeline({ input, role = 'customer' }: { input: OrderTimelineInput; role?: TimelineRole }) {
  const model = buildOrderTimeline(input, role);

  if (model.terminal) {
    const disputed = model.terminal.kind === 'disputed';
    return (
      <div
        className="card"
        style={{
          padding: 'var(--space-md)',
          border: `1px solid ${disputed ? 'rgba(255, 59, 48, 0.3)' : 'var(--color-separator)'}`,
        }}
      >
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: disputed ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
          {model.terminal.title}
        </p>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          {model.terminal.detail}
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      {model.stages.map((stage, i) => (
        <div key={stage.key} style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          {/* Rail: dot + connector */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: 3,
                background:
                  stage.state === 'done'
                    ? 'var(--color-success)'
                    : stage.state === 'current'
                      ? 'var(--color-primary)'
                      : 'var(--color-text-tertiary)',
                boxShadow:
                  stage.state === 'current' ? '0 0 0 3px rgba(255, 149, 0, 0.15)' : undefined,
              }}
            />
            {i < model.stages.length - 1 && (
              <span
                style={{
                  width: 2,
                  flex: 1,
                  minHeight: 14,
                  background: stage.state === 'done' ? 'var(--color-success)' : 'var(--color-separator)',
                  borderRadius: 1,
                }}
              />
            )}
          </div>
          {/* Label + timestamp */}
          <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 'var(--space-xs)' }}>
            <span
              style={{
                fontSize: 'var(--fs-body)',
                fontWeight: stage.state === 'current' ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                color: stage.state === 'upcoming' ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
              }}
            >
              {stage.label}
            </span>
            {stage.timestampUtc && (
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                {formatTs(stage.timestampUtc)}
              </span>
            )}
          </div>
        </div>
      ))}
      {model.nextAction && (
        <p
          style={{
            fontSize: 'var(--fs-caption)',
            color: 'var(--color-primary)',
            fontWeight: 'var(--fw-medium)',
            borderTop: '1px solid var(--color-separator)',
            paddingTop: 'var(--space-sm)',
          }}
        >
          {role === 'customer' ? 'Ваш шаг: ' : 'Следующий шаг: '}
          {model.nextAction}
        </p>
      )}
    </div>
  );
}

/* ── Compact strip (provider order cards) ─────────────────── */

export function OrderProgressStrip({ input, role = 'provider' }: { input: OrderTimelineInput; role?: TimelineRole }) {
  const model = buildOrderTimeline(input, role);

  if (model.terminal) {
    const disputed = model.terminal.kind === 'disputed';
    return (
      <p style={{ fontSize: 'var(--fs-caption)', color: disputed ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
        {model.terminal.title}. {model.terminal.detail}
      </p>
    );
  }

  const filled = model.stages.filter((s) => s.state !== 'upcoming').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {model.stages.map((stage) => (
          <span
            key={stage.key}
            title={stage.label + (stage.timestampUtc ? ` — ${formatTs(stage.timestampUtc)}` : '')}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background:
                stage.state === 'done'
                  ? 'var(--color-success)'
                  : stage.state === 'current'
                    ? 'var(--color-primary)'
                    : 'var(--color-separator)',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {filled} из {model.stages.length} этапов
        </span>
        {model.nextAction && (
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-primary)', fontWeight: 'var(--fw-medium)' }}>
            {model.nextAction}
          </span>
        )}
      </div>
    </div>
  );
}
